import { useEffect, useState } from "react";
import { Eyebrow, Headline } from "@/components/Editorial";
import { FullPageLoader } from "@/components/Loader";
import TopWedge from "@/components/TopWedge";
import { apiGet } from "@/lib/api";
import { fmtDate, fmtMoney } from "@/lib/format";
import { C } from "@/lib/brand";

const STATUS_COLORS = {
  processing: { bg: C.bone, fg: C.warmGray },
  shipped: { bg: C.peach, fg: C.peachDark },
  delivered: { bg: C.sage, fg: C.sageDark },
  cancelled: { bg: "#FCE5E0", fg: C.coral },
};

export default function Orders() {
  const [orders, setOrders] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    apiGet("/consumer/orders").then(setOrders).catch(() => setOrders([]));
  }, []);

  if (orders === null) return <FullPageLoader />;

  return (
    <div data-testid="orders-page" style={{ background: C.cream, paddingBottom: 80 }}>
      <section style={{ background: C.ink, color: C.cream, padding: "56px 24px 72px" }}>
        <div style={{ maxWidth: 900, margin: "0 auto" }}>
          <Eyebrow color={C.peach}>★ ORDER HISTORY</Eyebrow>
          <Headline before="YOUR" italic="orders." color={C.cream} italicColor={C.peach} size={64} className="mt-4" />
        </div>
      </section>
      <section style={{ padding: "52px 16px", position: "relative", background: C.cream }}>
        <TopWedge color={C.ink} dir="right" />
        <div style={{ maxWidth: 900, margin: "0 auto", position: "relative", zIndex: 2 }}>
          {orders.length === 0 && (
            <div style={{ padding: 48, textAlign: "center", color: C.warmGray }}>
              <p style={{ fontSize: 16, fontFamily: '"Instrument Serif", serif', fontStyle: "italic" }}>No orders yet.</p>
            </div>
          )}
          {orders.map((o) => {
            const open = expandedId === o.id;
            const colors = STATUS_COLORS[o.status] || { bg: C.bone, fg: C.warmGray };
            return (
              <div key={o.id} data-testid={`order-${o.orderNumber}`} style={{ background: C.pure, border: `1.5px solid ${C.warmGray3}`, marginBottom: 12 }}>
                <button
                  type="button"
                  data-testid={`order-row-${o.orderNumber}`}
                  onClick={() => setExpandedId(open ? null : o.id)}
                  style={{ width: "100%", textAlign: "left", padding: "20px 22px", display: "grid", gridTemplateColumns: "1.5fr 1fr 1fr 1fr 24px", gap: 12, alignItems: "center" }}
                >
                  <div>
                    <div style={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 700, fontSize: 13, color: C.ink }}>{o.orderNumber}</div>
                    <div style={{ marginTop: 4, fontSize: 12, color: C.warmGray }}>{fmtDate(o.createdAt)}</div>
                  </div>
                  <div style={{ fontSize: 13, color: C.warmGray }}>
                    {(o.items || []).length} item{(o.items || []).length === 1 ? "" : "s"}
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 800 }}>{fmtMoney(o.total)}</div>
                  <div>
                    <span style={{ display: "inline-block", padding: "5px 10px", background: colors.bg, color: colors.fg, fontSize: 10, fontWeight: 800, letterSpacing: "0.18em" }}>
                      {(o.status || "").toUpperCase()}
                    </span>
                  </div>
                  <div style={{ textAlign: "right", fontSize: 18, color: C.warmGray }}>{open ? "−" : "+"}</div>
                </button>

                {open && (
                  <div data-testid={`order-detail-${o.orderNumber}`} style={{ borderTop: `1px solid ${C.warmGray4}`, padding: "20px 22px", background: C.bone }}>
                    {(o.items || []).map((it, i) => (
                      <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", fontSize: 14, borderBottom: i < o.items.length - 1 ? `1px dashed ${C.warmGray3}` : "none" }}>
                        <div>
                          <div style={{ fontWeight: 700 }}>{it.productName}</div>
                          <div style={{ fontSize: 12, color: C.warmGray }}>{it.dosage} · qty {it.quantity}</div>
                        </div>
                        <div style={{ fontWeight: 700 }}>{fmtMoney(it.lineTotal)}</div>
                      </div>
                    ))}
                    <div style={{ marginTop: 14, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, fontSize: 13, color: C.warmGray }}>
                      <div>Subtotal</div><div style={{ textAlign: "right" }}>{fmtMoney(o.subtotal)}</div>
                      {o.discountAmount > 0 && (<><div>Discount</div><div style={{ textAlign: "right", color: C.sageDeep }}>−{fmtMoney(o.discountAmount)}</div></>)}
                      {o.creditApplied > 0 && (<><div>Credits applied</div><div style={{ textAlign: "right", color: C.sageDeep }}>−{fmtMoney(o.creditApplied)}</div></>)}
                      <div style={{ paddingTop: 10, borderTop: `1px solid ${C.warmGray3}`, color: C.ink, fontWeight: 800 }}>Total</div>
                      <div style={{ paddingTop: 10, borderTop: `1px solid ${C.warmGray3}`, textAlign: "right", color: C.ink, fontWeight: 800 }}>{fmtMoney(o.total)}</div>
                    </div>
                    {o.trackingNumber && (
                      <div style={{ marginTop: 16, fontSize: 13 }}>
                        <strong>{o.trackingCarrier || "Tracking"}:</strong> <span style={{ fontFamily: 'JetBrains Mono, monospace' }}>{o.trackingNumber}</span>
                      </div>
                    )}
                    {o.stripeHostedUrl && (
                      <a href={o.stripeHostedUrl} target="_blank" rel="noreferrer" data-testid={`order-receipt-${o.orderNumber}`} style={{ marginTop: 14, display: "inline-block", fontSize: 11, fontWeight: 800, letterSpacing: "0.18em", color: C.ink, textDecoration: "underline" }}>
                        VIEW STRIPE RECEIPT →
                      </a>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}
